import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import apiService from "../../../../../service/Api";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import PhoneNumberField from "../../../../../components/ui/PhoneNumberField";
import CustomSelect from "../../../../../components/ui/Select";
import CustomTextField from "../../../../../components/ui/Textfield";
import { RootState } from "../../../../../store/Index";
import { tokens } from "../../../../../theme";
import { IContactFormData } from "../../../../../types/contact";
import {
  BookingContactEntry,
  BookingData,
} from "../../../../../types/bookings";
import {
  DEFAULT_CONTACT_ROLE,
  PRIMARY_CONTACT_ROLE,
} from "../../../constants/contact-roles";
import BookingContactsField from "../../../components/BookingContactsField";
import {
  buildRowContact,
  withPrimaryMirrored,
} from "../../../utils/booking-contacts";
import { StepComponentProps } from "../../AddBookingV2";
import {
  ICompanyContactOption,
  IFuneralHomeBranchOption,
  useCompanyContactSearch,
  useCompanySearch,
  useContactSearch,
  IFuneralHomeCompanyOption,
  useReferralSearch,
  useFuneralHomeCompanies,
  useCompanyBranches,
} from "../../hooks/UseBookingsSearch";
import AddCompanyDialog from "../AddCompanyDialog";
import AddContactDialog from "../AddContactDialog";

import StepHeader from "../StepHeader";
import AddReferralDialog, { IReferralFormData } from "../AddReferralDialog";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PlaceIcon from "@mui/icons-material/Place";
import GroupsIcon from "@mui/icons-material/Groups";
import { PeopleAltOutlined } from "@mui/icons-material";

interface ICompanyOption {
  id: number;
  name: string;
}

interface ICompanyBranchOption {
  id: number;
  name: string;
}

const SOURCE_OPTIONS = [
  { value: "walk_in", label: "Walk In" },
  { value: "direct_call", label: "Direct Call" },
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "social_media", label: "Social Media" },
  { value: "client_portal", label: "Client Portal" },
];

// Business default: most funeral-home bookings come through this company/branch/rep.
const DEFAULT_FUNERAL_HOME_NAME_HINT = "Old Mutual";
const DEFAULT_BRANCH_NAME_HINT = "harare";
const DEFAULT_REP_NAME_HINT = "sarah masunda";

const SALUTATION_OPTIONS = [
  "Mr",
  "Mrs",
  "Ms",
  "Dr",
  "Rev",
  "Prof",
  "Sir",
  "Fr",
  "Pastor",
  "Hon",
];

const AVATAR_PALETTE = [
  { bg: "#2F6FED", fg: "#fff" },
  { bg: "#1B8A5A", fg: "#fff" },
  { bg: "#E07A1F", fg: "#fff" },
  { bg: "#7C4FE0", fg: "#fff" },
  { bg: "#D6455D", fg: "#fff" },
  { bg: "#0EA5A5", fg: "#fff" },
];

const getAvatarColor = (id: number) =>
  AVATAR_PALETTE[id % AVATAR_PALETTE.length];

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
const BookingClientInformation: React.FC<StepComponentProps> = ({
  bookingData,
  setBookingData,
  bookingType,
  isEdit,
  onUpdate,
  submitting,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const color400 = colors.primary[400] || "#724E41";
  const [, setFuneralHomeFlowConfirmed] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);
  const originalBookingTypeRef = React.useRef(bookingData.booking_type);
  const [openAddCompanyContactModal, setOpenAddCompanyContactModal] =
    useState(false);
  const [addCompanyContactStep, setAddCompanyContactStep] = useState<0 | 1>(0);
  const [modalCompanySearchTerm, setModalCompanySearchTerm] = useState("");
  const [modalCompanyOptions, setModalCompanyOptions] = useState<
    ICompanyOption[]
  >([]);
  const [modalCompanySearchLoading, setModalCompanySearchLoading] =
    useState(false);
  const [selectedModalCompany, setSelectedModalCompany] =
    useState<ICompanyOption | null>(null);
  const [modalBranches, setModalBranches] = useState<ICompanyBranchOption[]>(
    [],
  );
  const [modalBranchesLoading, setModalBranchesLoading] = useState(false);
  const [selectedModalBranch, setSelectedModalBranch] =
    useState<ICompanyBranchOption | null>(null);
  const [newCompanyContactForm, setNewCompanyContactForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [savingCompanyContact, setSavingCompanyContact] = useState(false);

  const { funeralHomeCompanies, funeralHomeLoading } =
    useFuneralHomeCompanies();

  const { branches: funeralHomeBranches } = useCompanyBranches(
    bookingData.funeral_home_id ?? undefined,
  );

  const [selectedFuneralHomeBranch, setSelectedFuneralHomeBranch] =
    useState<IFuneralHomeBranchOption | null>(null);

  useEffect(() => {
    setSelectedFuneralHomeBranch(null);
  }, [bookingData.funeral_home_id]);

  useEffect(() => {
    if (funeralHomeBranches.length === 1) {
      setSelectedFuneralHomeBranch(funeralHomeBranches[0]);
    }
  }, [funeralHomeBranches]);
  useEffect(() => {
    if (
      bookingData.client_type === "funeral_home" &&
      !bookingData.funeral_home_id &&
      !funeralHomeLoading &&
      funeralHomeCompanies.length > 0
    ) {
      const defaultCompany = funeralHomeCompanies.find((c) =>
        c.name
          .trim()
          .toLowerCase()
          .includes(DEFAULT_FUNERAL_HOME_NAME_HINT.toLowerCase()),
      );
      if (defaultCompany) {
        resetFuneralHomeSelection({
          funeral_home_id: defaultCompany.id,
          company_name: defaultCompany.name,
          branch_name: "",
        });
      }
      // No match found: do nothing. User just sees the normal gallery
      // with nothing preselected and picks manually — no broken state.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingData.client_type,
    bookingData.funeral_home_id,
    funeralHomeLoading,
    funeralHomeCompanies,
  ]);
  useEffect(() => {
    if (
      bookingData.company_name
        ?.trim()
        .toLowerCase()
        .includes(DEFAULT_FUNERAL_HOME_NAME_HINT.toLowerCase()) &&
      !selectedFuneralHomeBranch &&
      funeralHomeBranches.length > 0
    ) {
      const defaultBranch = funeralHomeBranches.find((b) =>
        b.name
          .trim()
          .toLowerCase()
          .includes(DEFAULT_BRANCH_NAME_HINT.toLowerCase()),
      );
      if (defaultBranch) {
        setSelectedFuneralHomeBranch(defaultBranch);
        setBookingData((prev) => ({
          ...prev,
          branch_name: defaultBranch.name,
        }));
      }
      // No matching branch found: nothing selected, user picks manually.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingData.company_name,
    funeralHomeBranches,
    selectedFuneralHomeBranch,
  ]);

  const {
    contactOptions,
    contactLoading,
    setContactSearchTerm,
    addContactToOptions,
  } = useContactSearch();

  const { addCompanyToOptions } = useCompanySearch();

  const { referralOptions, referralLoading, setReferralSearchTerm } =
    useReferralSearch();

  const { companyContactOptions, companyContactLoading } =
    useCompanyContactSearch(
      bookingData.funeral_home_id ?? undefined,
      bookingData.company_name ?? undefined,
    );

  const handleChange = (field: string, value: unknown) =>
    setBookingData((prev) => ({ ...prev, [field]: value as string }));

  const contactRows: BookingContactEntry[] = bookingData.contacts ?? [];

  const updateContacts = (
    nextContacts: BookingContactEntry[],
    extra: Partial<BookingData> = {},
  ) => setBookingData((prev) => withPrimaryMirrored(prev, nextContacts, extra));

  const handleContactSaved = (contact: IContactFormData) => {
    addContactToOptions(contact);
    const role =
      contactRows.length === 0 ? PRIMARY_CONTACT_ROLE : DEFAULT_CONTACT_ROLE;
    updateContacts([
      ...contactRows,
      {
        contact_id: Number(contact.id),
        role,
        contact: buildRowContact(contact),
      },
    ]);
  };

  const handleCompanySelect = (value: IContactFormData | null) => {
    setBookingData((prev) => ({
      ...prev,
      company_id: value ? Number(value.id) : null,
      company_name: value?.name || "",
      email: value?.email || "",
      phone: value?.phone || "",
    }));
  };

  const handleCompanySaved = (company: IContactFormData) => {
    addCompanyToOptions(company);
    handleCompanySelect(company);
  };

  // For funeral-home bookings the company contact is the primary (mirrored into
  // the legacy contact FK) and carries the "funeral_home" role. It is kept as
  // the first row so both the FE and backend mirror it as primary.
  const funeralHomeRoleRows = contactRows.filter(
    (r) => r.role === "funeral_home",
  );
  const additionalContactRows = contactRows.filter(
    (r) => r.role !== "funeral_home",
  );

  const commitAdditionalContacts = (additional: BookingContactEntry[]) =>
    updateContacts([...funeralHomeRoleRows, ...additional]);

  const handleSelectClientType = (type: "standard" | "funeral_home") => {
    if ((bookingData.client_type || "standard") === type) return;
    setFuneralHomeFlowConfirmed(false);
    setSelectedFuneralHomeBranch(null);
    setBookingData((prev) => ({
      ...prev,
      client_type: type,
      booking_type:
        type === "funeral_home"
          ? "FUNERAL_HOME"
          : 
            originalBookingTypeRef.current?.toUpperCase() === "FUNERAL_HOME"
            ? "FUNERAL"
            : originalBookingTypeRef.current,
      contacts: [],
      contact_id: null,
      first_name: "",
      surname: "",
      email: "",
      phone: "",
      company_name: "",
      branch_name: "",
      funeral_home_id: null,
    }));
  };

  const resetFuneralHomeSelection = (extra: Partial<BookingData> = {}) => {
    setBookingData((prev) => ({
      ...prev,
      contacts: additionalContactRows,
      contact_id: null,
      first_name: "",
      surname: "",
      email: "",
      phone: "",
      ...extra,
    }));
  };

  const handleCompanyContactSelect = (value: ICompanyContactOption | null) => {
    const row: BookingContactEntry | null = value
      ? {
          contact_id: value.id,
          role: "funeral_home",
          contact: {
            id: value.id,
            first_name: value.full_name?.split(" ")[0] || null,
            last_name: value.full_name?.split(" ").slice(1).join(" ") || null,
            name: value.full_name || null,
            email: value.email || null,
            phone: value.phone || null,
          },
        }
      : null;
    updateContacts(
      row ? [row, ...additionalContactRows] : additionalContactRows,
      {
        company_name: value?.company_name || bookingData.company_name || "",
        branch_name: value?.branch_name || "",
        funeral_home_id: value?.company_id ?? null,
      },
    );
  };

  const funeralHomeContactRow = funeralHomeRoleRows[0] || null;
  useEffect(() => {
    if (
      bookingData.company_name
        ?.trim()
        .toLowerCase()
        .includes(DEFAULT_FUNERAL_HOME_NAME_HINT.toLowerCase()) &&
      selectedFuneralHomeBranch?.name
        .trim()
        .toLowerCase()
        .includes(DEFAULT_BRANCH_NAME_HINT.toLowerCase()) &&
      !funeralHomeContactRow &&
      companyContactOptions.length > 0
    ) {
      const defaultRep = companyContactOptions.find((c) =>
        c.full_name
          .trim()
          .toLowerCase()
          .includes(DEFAULT_REP_NAME_HINT.toLowerCase()),
      );
      if (defaultRep) {
        handleCompanyContactSelect(defaultRep);
      }
      // No matching rep found: nothing selected, user picks manually.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingData.company_name,
    selectedFuneralHomeBranch,
    companyContactOptions,
    funeralHomeContactRow,
  ]);
  const handleReferralSaved = (referral: IReferralFormData) => {
    setBookingData((prev) => ({
      ...prev,
      referral_partner_id: referral.id,
      referral_partner_name:
        `${referral.referral_name} ${referral.referral_surname}`.trim(),
    }));
  };

  useEffect(() => {
    if (bookingData.source_of_booking !== "referral") {
      setBookingData((prev) => ({
        ...prev,
        referral_partner_id: null,
        referral_partner_name: null,
      }));
    }
  }, [bookingData.source_of_booking, setBookingData]);

  // Debounced company search for Add Company Contact modal
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (modalCompanySearchTerm.length < 2) {
        setModalCompanyOptions([]);
        return;
      }
      setModalCompanySearchLoading(true);
      try {
        const res = await apiService.get<{ results: ICompanyOption[] }>(
          `/api/companies/company/?search=${encodeURIComponent(modalCompanySearchTerm)}`,
        );
        setModalCompanyOptions(res.data.results || []);
      } catch {
        toast.error("Failed to search companies.");
      } finally {
        setModalCompanySearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [modalCompanySearchTerm]);

  // Fetch branches when a company is selected in the modal
  useEffect(() => {
    if (!selectedModalCompany) {
      setModalBranches([]);
      setSelectedModalBranch(null);
      return;
    }
    setModalBranchesLoading(true);
    apiService
      .get<{ results: ICompanyBranchOption[] }>(
        `/api/companies/company-branch/?company_id=${selectedModalCompany.id}`,
      )
      .then((res) => {
        const branches = res.data.results || [];
        setModalBranches(branches);
        setSelectedModalBranch(branches.length === 1 ? branches[0] : null);
      })
      .catch(() => toast.error("Failed to load branches."))
      .finally(() => setModalBranchesLoading(false));
  }, [selectedModalCompany]);

  const handleOpenAddCompanyContactModal = () => {
    setAddCompanyContactStep(0);
    setModalCompanySearchTerm("");
    setModalCompanyOptions([]);
    setSelectedModalCompany(null);
    setModalBranches([]);
    setSelectedModalBranch(null);
    setNewCompanyContactForm({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
    });
    setOpenAddCompanyContactModal(true);
  };

  const handleSaveNewCompanyContact = async () => {
    if (!selectedModalBranch) {
      toast.error("Please select a branch.");
      return;
    }
    if (!newCompanyContactForm.first_name || !newCompanyContactForm.last_name) {
      toast.error("First name and last name are required.");
      return;
    }
    setSavingCompanyContact(true);
    try {
      const res = await apiService.post<{
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        phone: string;
      }>("/api/contacts/contact/", {
        contact_type: "individual",
        first_name: newCompanyContactForm.first_name,
        last_name: newCompanyContactForm.last_name,
        email: newCompanyContactForm.email || "",
        phone: newCompanyContactForm.phone || "",
        company_branch_id: selectedModalBranch.id,
      });
      const saved = res.data;
      const row: BookingContactEntry = {
        contact_id: saved.id,
        role: "funeral_home",
        contact: {
          id: saved.id,
          first_name: saved.first_name || null,
          last_name: saved.last_name || null,
          name:
            `${saved.first_name || ""} ${saved.last_name || ""}`.trim() || null,
          email: saved.email || null,
          phone: saved.phone || null,
        },
      };
      updateContacts([row, ...additionalContactRows], {
        company_name: selectedModalCompany?.name || "",
        branch_name: selectedModalBranch.name,
      });
      toast.success(
        `Contact "${saved.first_name} ${saved.last_name}" created and selected.`,
      );
      setOpenAddCompanyContactModal(false);
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: unknown } })?.response
        ?.data;
      const msg =
        errData && typeof errData === "object"
          ? Object.values(errData).flat().join(" ")
          : "Failed to save company contact.";
      toast.error(msg);
    } finally {
      setSavingCompanyContact(false);
    }
  };

  const renderSourceField = () => (
    <CustomSelect
      label="Source of Booking"
      value={bookingData.source_of_booking || ""}
      onChange={(e) => handleChange("source_of_booking", e.target.value)}
    >
      {SOURCE_OPTIONS.map((s) => (
        <MenuItem key={s.value} value={s.value}>
          {s.label}
        </MenuItem>
      ))}
    </CustomSelect>
  );

  const renderSalutationField = () => (
    <CustomSelect
      label="Salutation"
      value={bookingData.client_salutation || ""}
      onChange={(e) => handleChange("client_salutation", e.target.value)}
    >
      {SALUTATION_OPTIONS.map((s) => (
        <MenuItem key={s} value={s}>
          {s}
        </MenuItem>
      ))}
    </CustomSelect>
  );

  // Top box: locked mode has nothing here, browse mode shows the logo
  // gallery, and once a company is picked it shows the banner + branch chips.

  // The actual rep picker — this is what now sits beside Source of Booking.

  const renderReferralField = () => {
    if (bookingData.source_of_booking !== "referral") return null; // ← no gap

    const selectedReferral =
      referralOptions.find((p) => p.id === bookingData.referral_partner_id) ||
      null;

    return (
      <Box>
        <Autocomplete
          fullWidth
          options={referralOptions}
          getOptionLabel={(o) =>
            `${o.referral_name} ${o.referral_surname}`.trim()
          }
          value={selectedReferral}
          onChange={(_e, value) =>
            setBookingData((prev) => ({
              ...prev,
              referral_partner_id: value ? value.id : null,
              referral_partner_name: value
                ? `${value.referral_name} ${value.referral_surname}`.trim()
                : null,
            }))
          }
          onInputChange={(_e, value) => setReferralSearchTerm(value)}
          loading={referralLoading}
          filterOptions={(x) => x}
          popupIcon={null}
          forcePopupIcon={false}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              label="Referred By"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {referralLoading && (
                      <CircularProgress size={18} sx={{ color: color400 }} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Typography
          variant="caption"
          sx={{ color: "#A0AEC0", display: "block", mt: 0.5, fontSize: "11px" }}
        >
          Type at least 3 characters to search referral partners
        </Typography>
        <Button
          startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
          onClick={() => setReferralDialogOpen(true)}
          sx={{
            color: color400,
            textTransform: "uppercase",
            fontSize: "12px",
            fontWeight: 700,
            p: 0,
            mt: 1,
            minWidth: 0,
            "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
          }}
        >
          Add Referral Partner
        </Button>
      </Box>
    );
  };

  const renderSpecialRequestAndNotes = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
        gap: 3,
        mb: 5,
      }}
    >
      <CustomTextField
        label="Special Request"
        value={bookingData.special_request || ""}
        onChange={(e) => handleChange("special_request", e.target.value)}
      />
      <CustomTextField
        label="Notes"
        value={bookingData.notes || ""}
        onChange={(e) => handleChange("notes", e.target.value)}
      />
      <Box />
    </Box>
  );
  const renderFields = () => {
    switch (bookingType) {
      // "funeral_home" is the same intake as "funeral" -- it is what a funeral
      // booking's client-type toggle saves as, so editing one must land back on
      // this block instead of falling through to `default: return null`.
      case "funeral_home":
      case "funeral": {
        const clientType =
          bookingData.client_type ||
          (bookingType === "funeral_home" ? "funeral_home" : "standard");
        const isFuneralHome = clientType === "funeral_home";
        const isLocked = !!bookingData.funeral_home_locked;

        return (
          <>
            {!isLocked && renderClientTypeToggle()}{" "}
            {isFuneralHome ? (
              <>
                {renderFuneralHomeStepPicker()}

                {!!funeralHomeContactRow && (
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      overflow: "hidden",
                      mb: 5,
                    }}
                  >
                    <Box
                      sx={{
                        px: 2.5,
                        py: 2,
                        bgcolor: "action.hover",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, fontSize: 13.5 }}
                      >
                        Family / Other Contacts
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontSize: 11.5 }}
                      >
                        Add family members or other people connected to this
                        booking.
                      </Typography>
                    </Box>

                    <Box sx={{ p: 2.5 }}>
                      <BookingContactsField
                        variant="card"
                        rows={additionalContactRows}
                        onChange={commitAdditionalContacts}
                        contactOptions={contactOptions}
                        contactLoading={contactLoading}
                        setContactSearchTerm={setContactSearchTerm}
                        onNewContactClick={() => setContactDialogOpen(true)}
                        firstIsPrimary={false}
                        defaultRole="family"
                      />
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm:
                        bookingData.source_of_booking === "referral"
                          ? "1fr 1fr"
                          : "1fr",
                    },
                    gap: 3,
                    mb: 3,
                    alignItems: "start",
                  }}
                >
                  {renderSourceField()}
                  {renderReferralField()}
                </Box>
                <Box sx={{ mb: 3 }}>
                  <BookingContactsField
                    rows={contactRows}
                    onChange={updateContacts}
                    contactOptions={contactOptions}
                    contactLoading={contactLoading}
                    setContactSearchTerm={setContactSearchTerm}
                    onNewContactClick={() => setContactDialogOpen(true)}
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                    gap: 3,
                    mb: 3,
                  }}
                >
                  <CustomTextField
                    label="Email"
                    value={bookingData.email || ""}
                    onChange={(e) => handleChange("email", e.target.value)}
                  />
                  <PhoneNumberField
                    label="Phone"
                    value={bookingData.phone || ""}
                    onChange={(value) => handleChange("phone", value)}
                  />
                  <CustomSelect
                    label="Relationship to Deceased"
                    value={bookingData.relationship_to_deceased || ""}
                    onChange={(e) =>
                      handleChange("relationship_to_deceased", e.target.value)
                    }
                  >
                    {[
                      "son",
                      "daughter",
                      "father",
                      "mother",
                      "brother",
                      "husband",
                      "wife",
                      "sister",
                      "son_in_law",
                      "daughter_in_law",
                      "friend",
                      "cousin",
                      "grand_son",
                      "grand_daughter",
                      "other",
                    ].map((v) => (
                      <MenuItem key={v} value={v}>
                        {v
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                    gap: 3,
                    mb: 5,
                  }}
                >
                  <CustomTextField
                    label="Special Request"
                    value={bookingData.special_request || ""}
                    onChange={(e) =>
                      handleChange("special_request", e.target.value)
                    }
                  />
                  <CustomTextField
                    label="Notes"
                    value={bookingData.notes || ""}
                    onChange={(e) => handleChange("notes", e.target.value)}
                  />
                  <Box />
                </Box>
              </>
            )}
          </>
        );
      }
      case "church":
        return (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 3,
                mb: 3,
                alignItems: "start",
              }}
            >
              {renderSourceField()}
              {renderReferralField()}
              <BookingContactsField
                rows={contactRows}
                onChange={updateContacts}
                contactOptions={contactOptions}
                contactLoading={contactLoading}
                setContactSearchTerm={setContactSearchTerm}
                onNewContactClick={() => setContactDialogOpen(true)}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <CustomTextField
                label="Surname"
                value={bookingData.surname || ""}
                onChange={(e) => handleChange("surname", e.target.value)}
                disabled
              />
              <CustomTextField
                label="Email"
                value={bookingData.email || ""}
                onChange={(e) => handleChange("email", e.target.value)}
              />
              <PhoneNumberField
                label="Phone"
                value={bookingData.phone || ""}
                onChange={(value) => handleChange("phone", value)}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 3,
                mb: 5,
              }}
            >
              <CustomTextField
                label="Special Request"
                value={bookingData.special_request || ""}
                onChange={(e) =>
                  handleChange("special_request", e.target.value)
                }
              />
              <CustomTextField
                label="Notes"
                value={bookingData.notes || ""}
                onChange={(e) => handleChange("notes", e.target.value)}
              />
              <Box />
            </Box>
          </>
        );

      case "event":
      case "other":
      case "conference":
        return (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 3,
                mb: 3,
                alignItems: "start",
              }}
            >
              {renderSourceField()}
              {renderReferralField()}
              {renderSalutationField()}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 3,
                mb: 3,
                alignItems: "start",
              }}
            >
              <BookingContactsField
                rows={contactRows}
                onChange={updateContacts}
                contactOptions={contactOptions}
                contactLoading={contactLoading}
                setContactSearchTerm={setContactSearchTerm}
                onNewContactClick={() => setContactDialogOpen(true)}
              />
              <CustomTextField
                label="Surname"
                value={bookingData.surname || ""}
                onChange={(e) => handleChange("surname", e.target.value)}
                disabled
              />
              <CustomTextField
                label="Email"
                value={bookingData.email || ""}
                onChange={(e) => handleChange("email", e.target.value)}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <PhoneNumberField
                label="Phone"
                value={bookingData.phone || ""}
                onChange={(value) => handleChange("phone", value)}
              />
              <Box />
              <Box />
            </Box>
            {renderSpecialRequestAndNotes()}
          </>
        );

      default:
        return null;
    }
  };
  const renderPickerStepHeader = (
    stepNumber: number,
    title: string,
    icon: React.ReactNode,
    completed: boolean,
  ) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 700,
          flexShrink: 0,
          ...(completed
            ? { bgcolor: "#1B8A5A", color: "#fff" }
            : { bgcolor: "action.hover", color: "text.secondary" }),
        }}
      >
        {completed ? "✓" : stepNumber}
      </Box>
      <Box
        sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 15 }}>
        {title}
      </Typography>
    </Box>
  );

  function renderSelectableCards<T>(
    items: T[],
    opts: {
      getId: (item: T) => number | string;
      isSelected: (item: T) => boolean;
      onSelect: (item: T) => void;
      getTitle: (item: T) => string;
      getSubtitle?: (item: T) => string;
      getAvatar?: (item: T) => React.ReactNode;
      minWidth?: number;
    },
  ) {
    const minWidth = opts.minWidth ?? 170;
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`,
          gap: 1,
        }}
      >
        {items.map((item) => {
          const selected = opts.isSelected(item);
          return (
            <Box
              key={opts.getId(item)}
              onClick={() => opts.onSelect(item)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.1,
                height: 56,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: selected ? "#1B8A5A" : "divider",
                bgcolor: selected ? "rgba(27,138,90,0.06)" : "background.paper",
                cursor: "pointer",
                transition: "all 120ms ease",
                minWidth: 0,
                "&:hover": {
                  borderColor: selected ? "#1B8A5A" : "primary.main",
                },
              }}
            >
              {opts.getAvatar?.(item)}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: 12.5,
                    lineHeight: 1.25,
                    display: "-webkit-box",
                    WebkitLineClamp: opts.getSubtitle ? 1 : 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {opts.getTitle(item)}
                </Typography>
                {opts.getSubtitle && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: 10.5,
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {opts.getSubtitle(item)}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }
  const renderClientTypeToggle = () => {
    const clientType = bookingData.client_type || "standard";
    const options: {
      value: "standard" | "funeral_home";
      title: string;
      description: string;
      icon: React.ReactNode;
      iconBg: string;
    }[] = [
      {
        value: "standard",
        title: "Standard Booking",
        description:
          "Direct booking from an individual family. Quick intake with client search.",
        icon: <PeopleAltOutlined sx={{ fontSize: 20, color: "#fff" }} />,
        iconBg: "#1B8A5A",
      },
      {
        value: "funeral_home",
        title: "Funeral Home Booking",
        description:
          "Booking received through a partnered funeral home with branch selection.",
        icon: <ApartmentIcon sx={{ fontSize: 20, color: "#fff" }} />,
        iconBg: "#C9820F",
      },
    ];

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            fontSize: 12,
            mb: 1,
          }}
        >
          Choose the intake path for this booking.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.5,
          }}
        >
          {options.map((opt) => {
            const active = clientType === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => handleSelectClientType(opt.value)}
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: active ? "#1B8A5A" : "divider",
                  bgcolor: active ? "rgba(27,138,90,0.05)" : "background.paper",
                  cursor: "pointer",
                  transition: "all 120ms ease",
                  "&:hover": { borderColor: "#1B8A5A" },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: opt.iconBg,
                    mb: 1,
                  }}
                >
                  {opt.icon}
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: 14.5,
                    mb: 0.25,
                    color: active ? "#1B8A5A" : "text.primary",
                  }}
                >
                  {opt.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: 12.5,
                    lineHeight: 1.4,
                  }}
                >
                  {opt.description}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };
  const renderFuneralHomeStepPicker = () => {
    const isLocked = !!bookingData.funeral_home_locked;
    const companySelected = !!bookingData.funeral_home_id;
    const branchSelected = !!selectedFuneralHomeBranch;
    const repSelected = !!funeralHomeContactRow;
    const stepOffset = isLocked ? 1 : 0;

    const branchFilteredReps = selectedFuneralHomeBranch
      ? companyContactOptions.filter(
          (o) => o.branch_name === selectedFuneralHomeBranch.name,
        )
      : companyContactOptions;

    const handleSelectCompany = (c: IFuneralHomeCompanyOption) => {
      setFuneralHomeFlowConfirmed(false);
      setSelectedFuneralHomeBranch(null);
      resetFuneralHomeSelection({
        funeral_home_id: c.id,
        company_name: c.name,
        branch_name: "",
      });
    };

    const handleSelectBranch = (b: IFuneralHomeBranchOption) => {
      setFuneralHomeFlowConfirmed(false);
      setSelectedFuneralHomeBranch(b);
      setBookingData((prev) => ({ ...prev, branch_name: b.name }));
    };

    return (
      <Box sx={{ mb: 4 }}>
        {isLocked ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              mb: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: "#1B8A5A",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {getInitials(bookingData.company_name || "")}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", fontSize: 11 }}
              >
                Funeral Home
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: 13.5 }}
              >
                {bookingData.company_name}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            {renderPickerStepHeader(
              1,
              "Choose Funeral Home",
              <ApartmentIcon fontSize="small" />,
              companySelected,
            )}
            {funeralHomeLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} sx={{ color: color400 }} />
              </Box>
            ) : (
              renderSelectableCards(funeralHomeCompanies, {
                getId: (c) => c.id,
                isSelected: (c) => bookingData.funeral_home_id === c.id,
                onSelect: handleSelectCompany,
                getTitle: (c) => c.name,
                getAvatar: (c) => {
                  const palette = getAvatarColor(c.id);
                  return (
                    <Avatar
                      src={c.company_logo || undefined}
                      variant="rounded"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: c.company_logo ? "transparent" : palette.bg,
                        color: palette.fg,
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {!c.company_logo && getInitials(c.name)}
                    </Avatar>
                  );
                },
              })
            )}
          </Box>
        )}

        {companySelected && (
          <Box sx={{ mb: 3 }}>
            {renderPickerStepHeader(
              2 - stepOffset,
              "Choose Branch",
              <PlaceIcon fontSize="small" />,
              branchSelected,
            )}
            {funeralHomeBranches.length > 0 ? (
              renderSelectableCards(funeralHomeBranches, {
                getId: (b) => b.id,
                isSelected: (b) => selectedFuneralHomeBranch?.id === b.id,
                onSelect: handleSelectBranch,
                getTitle: (b) => b.name,
                getSubtitle: (b) =>
                  (
                    b as IFuneralHomeBranchOption & {
                      region?: string;
                      address?: string;
                    }
                  ).region ||
                  (
                    b as IFuneralHomeBranchOption & {
                      region?: string;
                      address?: string;
                    }
                  ).address ||
                  "",
              })
            ) : (
              <Typography variant="caption" sx={{ color: "#A0AEC0" }}>
                No branches found for this company.
              </Typography>
            )}
          </Box>
        )}

        {companySelected && branchSelected && (
          <Box sx={{ mb: 3 }}>
            {renderPickerStepHeader(
              3 - stepOffset,
              "Choose Branch Representative",
              <GroupsIcon fontSize="small" />,
              repSelected,
            )}
            {companyContactLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} sx={{ color: color400 }} />
              </Box>
            ) : branchFilteredReps.length > 0 ? (
              renderSelectableCards(branchFilteredReps, {
                getId: (r) => r.id,
                isSelected: (r) => funeralHomeContactRow?.contact_id === r.id,
                onSelect: (r) => handleCompanyContactSelect(r),
                getTitle: (r) => r.full_name,
                getSubtitle: (r) => r.role || "",
                getAvatar: (r) => {
                  const palette = getAvatarColor(r.id);
                  return (
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: palette.bg,
                        color: palette.fg,
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {getInitials(r.full_name)}
                    </Avatar>
                  );
                },
              })
            ) : (
              <Typography variant="caption" sx={{ color: "#A0AEC0" }}>
                No representatives found for this branch.
              </Typography>
            )}
            <Button
              startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
              onClick={handleOpenAddCompanyContactModal}
              sx={{
                color: color400,
                textTransform: "uppercase",
                fontSize: "12px",
                fontWeight: 700,
                p: 0,
                mt: 1.5,
                minWidth: 0,
                "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
              }}
            >
              Add New Representative
            </Button>
          </Box>
        )}
      </Box>
    );
  };
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <StepHeader
          title="Client Information"
          isEdit={isEdit}
          onUpdate={onUpdate}
          submitting={submitting}
        />
        {renderFields()}
      </Box>

      <AddContactDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        onSaved={handleContactSaved}
      />
      <AddCompanyDialog
        open={companyDialogOpen}
        onClose={() => setCompanyDialogOpen(false)}
        onSaved={handleCompanySaved}
      />
      <AddReferralDialog
        open={referralDialogOpen}
        onClose={() => setReferralDialogOpen(false)}
        onSaved={handleReferralSaved}
      />

      {/* Add Company Contact — 2-step modal */}
      <Dialog
        open={openAddCompanyContactModal}
        onClose={() => setOpenAddCompanyContactModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Company Contact
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Step {addCompanyContactStep + 1} of 2 —{" "}
            {addCompanyContactStep === 0 ? "Select Company" : "Contact Details"}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {addCompanyContactStep === 0 ? (
            <Box sx={{ pt: 1 }}>
              <Autocomplete
                fullWidth
                options={modalCompanyOptions}
                getOptionLabel={(o: ICompanyOption) => o.name}
                value={selectedModalCompany}
                onChange={(_e, value) => setSelectedModalCompany(value)}
                onInputChange={(_e, value) => setModalCompanySearchTerm(value)}
                loading={modalCompanySearchLoading}
                filterOptions={(x) => x}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                noOptionsText={
                  modalCompanySearchTerm.length < 2
                    ? "Type to search companies"
                    : "No companies found"
                }
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Search Company"
                    variant="outlined"
                    helperText="Type at least 2 characters to search"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {modalCompanySearchLoading && (
                            <CircularProgress
                              size={18}
                              sx={{ color: color400 }}
                            />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          ) : (
            <Box
              sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {/* Selected company banner */}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedModalCompany?.name}
                </Typography>
              </Box>

              {/* Branch selector */}
              {modalBranchesLoading ? (
                <CircularProgress size={24} sx={{ alignSelf: "center" }} />
              ) : modalBranches.length > 1 ? (
                <Autocomplete
                  fullWidth
                  options={modalBranches}
                  getOptionLabel={(o: ICompanyBranchOption) => o.name}
                  value={selectedModalBranch}
                  onChange={(_e, value) => setSelectedModalBranch(value)}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      label="Select Branch *"
                      variant="outlined"
                    />
                  )}
                />
              ) : modalBranches.length === 1 ? (
                <CustomTextField
                  label="Branch"
                  value={modalBranches[0].name}
                  disabled
                  variant="outlined"
                  fullWidth
                />
              ) : (
                <Typography variant="caption" color="error">
                  No branches found for this company.
                </Typography>
              )}

              {/* Contact detail fields */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="First Name *"
                    variant="outlined"
                    value={newCompanyContactForm.first_name}
                    onChange={(e) =>
                      setNewCompanyContactForm((p) => ({
                        ...p,
                        first_name: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="Last Name *"
                    variant="outlined"
                    value={newCompanyContactForm.last_name}
                    onChange={(e) =>
                      setNewCompanyContactForm((p) => ({
                        ...p,
                        last_name: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="Email"
                    type="email"
                    variant="outlined"
                    value={newCompanyContactForm.email}
                    onChange={(e) =>
                      setNewCompanyContactForm((p) => ({
                        ...p,
                        email: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="Phone"
                    variant="outlined"
                    value={newCompanyContactForm.phone}
                    onChange={(e) =>
                      setNewCompanyContactForm((p) => ({
                        ...p,
                        phone: e.target.value,
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenAddCompanyContactModal(false)}
            disabled={savingCompanyContact}
          >
            Cancel
          </Button>
          {addCompanyContactStep === 0 ? (
            <Button
              variant="contained"
              onClick={() => setAddCompanyContactStep(1)}
              disabled={!selectedModalCompany}
            >
              Next
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setAddCompanyContactStep(0)}
                disabled={savingCompanyContact}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveNewCompanyContact}
                disabled={
                  savingCompanyContact ||
                  !selectedModalBranch ||
                  !newCompanyContactForm.first_name ||
                  !newCompanyContactForm.last_name
                }
                startIcon={
                  savingCompanyContact ? (
                    <CircularProgress size={16} />
                  ) : undefined
                }
              >
                Save Contact
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingClientInformation;
